import {
  type Resident,
  type ResidentItem,
  ResidentStatus
} from '@lmc/ts-common'
import { Avatar, BackButton, CollapsibleItem, Heading, Menu } from '@lmc/ui-kit'
import { ConfigProvider } from 'antd'
import { startCase } from 'lodash'
import type React from 'react'
import { useState } from 'react'
import styled, { css } from 'styled-components'
import 'isomorphic-fetch'
import { ListItem } from '@lmc/ui-kit/src/dataDisplay'
import type { ToggleCollapseFn } from '@lmc/ui-kit/src/dataDisplay/CollapsibleItem/CollapsibleItem'
import Search from '@lmc/ui-kit/src/dataEntry/Input/components/Search/Search'
import { Text } from '@lmc/ui-kit/src/general'
import Divider from '@lmc/ui-kit/src/layout/Divider/Divider'
import addTransparencyToHex from '@lmc/ui-kit/src/utils/addTransparencyToHex/addTransparencyToHex'
import type { ItemType } from 'antd/lib/menu/hooks/useItems'
import { Spinner } from 'components/generic'
import useResident from 'hooks/useResident'
import useResidentsList from 'hooks/useResidentsList'
import { NAMESPACE } from 'locale'
import { generalConstants } from 'locale/general'
import { useTranslation } from 'react-i18next'
import { Link, type Params, useLocation, useParams } from 'react-router-dom'
import {
  type CustomMenuItemType,
  getNavigationMenuItems,
  navigationTheme
} from 'utils'
import { useResidentsSidebarNavigation } from './utils'

type ResidentsSidebarNavigationProps = {
  menuItems?: ItemType &
    {
      label: string
      url: string
      icon: React.ReactNode
      subItems: ItemType &
        {
          label: string
          url: string
          icon: React.ReactNode
          tag: React.ReactNode
        }[]
    }[]
  preferredPeopleNominative: string
  resident?: Resident
  navigate: (key: string) => void
  testId?: string
}

const profileRoutes = [
  'care-related-information',
  'contact-details',
  'personal-information',
  'site-related-information'
]

const fullUrlsNavigation = ['gp-connect']

const isProfileRoute = (path: string) => {
  return profileRoutes.some((route) => path.endsWith(route))
}

export const getLink = (
  residentID: string,
  params: Params<string>,
  pathname: string
) => {
  let newPath = pathname

  if (!params.id || !newPath.includes(params.id)) {
    return `/residents/${residentID}/profile`
  }

  if (params.id) {
    newPath = newPath.replace(params.id, residentID)

    const stringAfterResidentID = newPath?.split(residentID)[1]

    const fullUrlString = fullUrlsNavigation.some((substring) =>
      stringAfterResidentID.includes(substring)
    )
    const routeString = fullUrlString
      ? stringAfterResidentID?.slice(1)
      : stringAfterResidentID?.split('/')[1]

    newPath = newPath.replace(stringAfterResidentID, `/${routeString}`)
    if (isProfileRoute(newPath)) {
      return newPath.replace(stringAfterResidentID, '/profile')
    }
  }
  return newPath
}

const getName = (item: ResidentItem) => `${item.name.first} ${item.name.last}`

const ResidentsSidebarNavigation: React.FC<ResidentsSidebarNavigationProps> = ({
  menuItems,
  preferredPeopleNominative,
  navigate,
  testId
}) => {
  const { id } = useParams()
  const { resident, isLoading } = useResident({ id })
  const items = getNavigationMenuItems(
    menuItems as unknown as CustomMenuItemType[]
  )

  const { defaultOpenKey, defaultSelectedKey } = useResidentsSidebarNavigation()

  const { activeResidents: residents, isLoading: isLoadingResidents } =
    useResidentsList({
      options: {
        onSuccess: (data) => {
          setShownResidents(
            data.result.filter(
              (resident) => resident.status === ResidentStatus.active
            )
          )
        }
      }
    })

  const [shownResidents, setShownResidents] = useState<ResidentItem[]>(
    residents || []
  )

  const [isHeaderCollapsed, setIsHeaderCollapsed] = useState(true)

  const params = useParams()
  const { pathname } = useLocation()

  const onSearchChange = (event: { target: { value: string } }) => {
    const { value } = event.target

    const newShownResidents = residents.filter((residentItem) => {
      const name = getName(residentItem).toLocaleLowerCase()

      return name.includes(value.toLocaleLowerCase())
    })

    setShownResidents(newShownResidents)
  }

  const { t } = useTranslation(NAMESPACE.GENERAL)

  return (
    <StyledContainer
      data-testid={
        testId ? `${testId}-sidebar-navigation` : 'residents-sidebar-navigation'
      }
    >
      <StyledHeader
        data-testid={
          testId ? `${testId}-sidebar-header` : 'residents-sidebar-header'
        }
      >
        <BackButtonStyled
          onClick={() => navigate('list')}
          data-testid={
            testId ? `${testId}-back-button` : 'residents-back-button'
          }
        >
          {`${preferredPeopleNominative} list`}
        </BackButtonStyled>
        {isLoading || !resident || isLoadingResidents ? (
          <Spinner
            data-testid={
              testId ? `${testId}-loading-spinner` : 'residents-loading-spinner'
            }
          />
        ) : (
          <CollapsibleItemStyled
            onCollapseChange={(collapse) => setIsHeaderCollapsed(collapse)}
            data-testid={
              testId ? `${testId}-resident-info` : 'residents-resident-info'
            }
            header={(toggleCollapse: ToggleCollapseFn, collapsed: boolean) => (
              <ListItemStyled
                onClick={toggleCollapse}
                data-testid={
                  testId
                    ? `${testId}-resident-header`
                    : 'residents-resident-header'
                }
                icon={
                  <Avatar
                    url={resident?.picture as unknown as string}
                    size={38}
                    data-testid={
                      testId
                        ? `${testId}-resident-avatar`
                        : 'residents-resident-avatar'
                    }
                  />
                }
                hasRoundIcon
                title={
                  <div
                    style={{
                      marginTop: 8,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    data-testid={
                      testId
                        ? `${testId}-resident-info-container`
                        : 'resident-info-container'
                    }
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        marginLeft: 8
                      }}
                      data-testid={
                        testId
                          ? `${testId}-resident-details`
                          : 'resident-details'
                      }
                    >
                      <Heading
                        level={4}
                        data-testid={
                          testId
                            ? `${testId}-resident-preferred-name`
                            : 'resident-preferred-name'
                        }
                      >
                        {resident?.preferredName || resident?.name?.first}
                      </Heading>
                      <Text
                        data-testid={
                          testId
                            ? `${testId}-resident-full-name`
                            : 'resident-full-name'
                        }
                      >
                        {`${resident?.name?.first} ${resident?.name?.last}`}
                      </Text>
                      <StyledText
                        data-testid={
                          testId
                            ? `${testId}-resident-status`
                            : 'resident-status'
                        }
                      >
                        {startCase(resident?.status)}
                      </StyledText>
                    </div>
                  </div>
                }
                toggleCollapse={toggleCollapse}
                isCollapsed={collapsed}
              />
            )}
          >
            <ScrollableContainer
              data-testid={
                testId
                  ? `${testId}-residents-list-container`
                  : 'residents-list-container'
              }
            >
              <Search
                placeholder={t(generalConstants.TYPE_TO_SEARCH)}
                onChange={onSearchChange}
                data-testid={
                  testId ? `${testId}-residents-search` : 'residents-search'
                }
              />
              <Divider />
              {shownResidents.length === 0 ? (
                <Text
                  data-testid={
                    testId
                      ? `${testId}-no-residents-found`
                      : 'sidebar-no-residents-found'
                  }
                >
                  {`No ${preferredPeopleNominative.toLocaleLowerCase()} found`}
                </Text>
              ) : (
                <div
                  data-testid={
                    testId ? `${testId}-residents-list` : 'sidebar-residents'
                  }
                >
                  {shownResidents.map((resident, index) => (
                    <LinkWithoutStyles
                      key={resident.id}
                      to={getLink(resident.id, params, pathname)}
                      data-testid={
                        testId
                          ? `${testId}-resident-link-${index}`
                          : `resident-link-${index}`
                      }
                    >
                      <ListItemResident
                        icon={
                          <Avatar
                            size={40}
                            url={resident.picture as unknown as string}
                            data-testid={
                              testId
                                ? `${testId}-resident-avatar-${index}`
                                : `resident-avatar-${index}`
                            }
                          />
                        }
                        title={<Text>{getName(resident)}</Text>}
                        data-testid={
                          testId
                            ? `${testId}-resident-item-${index}`
                            : `resident-item-${index}`
                        }
                      />
                    </LinkWithoutStyles>
                  ))}
                </div>
              )}
            </ScrollableContainer>
          </CollapsibleItemStyled>
        )}
      </StyledHeader>
      <StyledMenuContainer
        isHidden={!isHeaderCollapsed}
        data-testid={
          testId ? `${testId}-menu-container` : 'residents-menu-container'
        }
      >
        <ConfigProvider theme={navigationTheme}>
          <Menu
            data-cy="residents-menu"
            data-testid={
              testId ? `${testId}-navigation-menu` : 'residents-navigation-menu'
            }
            onClick={({ key }) => navigate(`${resident?.id}/${key}`)}
            style={{ border: 'none' }}
            defaultSelectedKeys={[defaultSelectedKey]}
            selectedKeys={[defaultSelectedKey]}
            defaultOpenKeys={[defaultOpenKey]}
            mode="inline"
            theme="light"
            inlineCollapsed={false}
            items={items}
          />
        </ConfigProvider>
      </StyledMenuContainer>
    </StyledContainer>
  )
}

const ListItemStyled = styled(ListItem)`
  padding: 8px 16px;

  cursor: pointer;
`

const ListItemResident = styled(ListItem)`
  padding: 4px;

  border-radius: ${({ theme }) => {
    const { large, medium } = theme.properties.borderRadius

    return `${large} ${medium} ${medium} ${large}`
  }};

  :hover {
    background-color: ${(props) =>
      addTransparencyToHex(props.theme.color.status.primary, 10)};
  }
`

const CollapsibleItemStyled = styled(CollapsibleItem)`
  border: 1px solid ${(props) => props.theme.color.neutral.color7};

  border-radius: ${(props) => props.theme.properties.borderRadius.medium};

  :hover {
    background-color: ${(props) => props.theme.color.neutral.color10};
  }
`
const LinkWithoutStyles = styled(Link)`
  color: initial;

  :hover {
    color: initial;
  }
  :focus {
    color: initial;
    text-decoration: none;
  }
`

const ScrollableContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0px 16px;
`

const BackButtonStyled = styled(BackButton)`
  margin-left: 16px;
`

const StyledContainer = styled.div`
  margin-bottom: 0px;
  background-color: ${(props) => props.theme.color.neutral.color11} !important;
  max-width: 290px;
  width: 100%;
  height: 100%;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    margin-right: 24px;
    margin-left: 24px;
    max-width: unset;
    width: 100%;
  }
  overflow: auto;
`

const show = css`
  @keyframes show {
    0% {
      visibility: hidden;
      opacity: 0;
      max-height: 0;
    }
    1% {
      visibility: visible;
    }
    100% {
      visibility: visible;
      opacity: 1;
      max-height: 1000px;
    }
  }
`

const hide = css`
  @keyframes hide {
    0% {
      visibility: visible;
      opacity: 1;
      max-height: 1000px;
    }
    99% {
      visibility: visible;
    }
    100% {
      visibility: hidden;
      opacity: 0;
      max-height: 0;
    }
  }
`

const StyledMenuContainer = styled.div<{ isHidden: boolean }>`
  transition:
    visibility 0.2s linear,
    opacity 0.2s linear;

  ${(props) =>
    props.isHidden &&
    css`
      visibility: hidden;
      opacity: 0;

      max-height: 0px;
    `}

  overflow-y: auto;
  ${({ theme }) => theme.mediaQueries.mobileDown} {
    max-height: unset;
  }

  animation: ${(props) =>
    props.isHidden
      ? `${show} 0.5s linear forwards`
      : `${hide} 0.5s linear forwards`};
`

const StyledHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 8px;
  border-bottom: 1px solid ${(props) => props.theme.color.neutral.color8};
`

const StyledText = styled(Text)`
  font-size: 12px !important;
  line-height: 15px;
  color: ${(props) => props.theme.color.status.success} !important;
`

export default ResidentsSidebarNavigation
